import {Form, List, Modal, Select} from 'antd';
import React from 'react';

import {useAdminGroupSaleListQuery, usePostAdminAssignProductsToGroupSaleMutation} from '../../../../gen/api/apis';
import {useNotification} from '../../../../NotificationContext';

export type ModalGroupSaleProduct = {
    id: string;
    name: string;
    listingNumber: string;
    asin: string;
};

export type GroupSaleAssignModalProps = {
    products: ModalGroupSaleProduct[];
    onClosed: () => void;
};

export const GroupSaleAssignModal = (props: GroupSaleAssignModalProps) => {
    const notify = useNotification();
    const [form] = Form.useForm();
    const groupSaleID = Form.useWatch('groupSaleID', form);

    const queryGroupSale = useAdminGroupSaleListQuery({
        refetchOnMount: true,
    });

    const assignProductsToGroupSale = usePostAdminAssignProductsToGroupSaleMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('success', 'Products assigned to group sale successfully');
            props.onClosed();
        },
        onError: (error) => {
            notify.openNotificationWithIcon('error', 'Error assigning products to group sale');
            console.error(error);
        },
    });

    const assign = () => {
        form.validateFields()
            .then((values) => {
                assignProductsToGroupSale.mutate({
                    params: {
                        groupSaleId: values.groupSaleID,
                        productIds: props.products.map((product) => product.id),
                    },
                });
            })
            .catch((error) => {
                notify.openNotificationWithIcon('error', 'Validation failed');
                console.error(error);
            });
    };

    return (
        <Modal
            open
            onCancel={props.onClosed}
            onOk={assign}
            title={'Assign Products to Group Sale'}
            okText="Assign"
            cancelText="Cancel"
            width={1000}
            centered
            destroyOnClose
            modalRender={(dom) => (
                <Form
                    layout="vertical"
                    labelCol={{span: 6}}
                    wrapperCol={{span: 18}}
                    form={form}
                    name="newOrder"
                    clearOnDestroy
                    onFinish={() => assign()}
                >
                    {dom}
                </Form>
            )}
        >
            <Form.Item
                name="groupSaleID"
                rules={[{required: true, message: 'Please select a group sale'}]}
            >
                <Select
                    showSearch
                    placeholder="Select a group sale"
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    options={
                        queryGroupSale.data?.groupSales.map((sale) => ({
                            value: sale.id,
                            label: sale.name,
                        })) || []
                    }
                    onChange={(value) => {
                        form.setFieldsValue({groupSaleID: value});
                    }}
                />
            </Form.Item>

            <List
                size="small"
                header="Assignable Products"
                style={{marginBottom: 20}}
                bordered
                dataSource={props.products}
                renderItem={(item) => (
                    // trim with ...
                    <List.Item style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        [{item.listingNumber} / {item.asin}] - {item.name}
                    </List.Item>
                )}
            />
            {groupSaleID && (
                <List
                    size="small"
                    header="Already in the group sale"
                    bordered
                    dataSource={queryGroupSale.data?.groupSales.find((g) => g.id === groupSaleID)?.products || []}
                    renderItem={(item) => (
                        // trim with ...
                        <List.Item style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            [{item.listingNumber} / {item.asin}] - {item.name}
                        </List.Item>
                    )}
                />
            )}
        </Modal>
    );
};
